import React, { useState, useEffect, useMemo, useRef } from "react";
import { extent, group, rollups, sum } from "d3-array";
import { scaleLinear } from "d3-scale";
import debounce from "lodash.debounce";
import sortBy from "lodash.sortby";

import Map, { stackLocations } from "./Map";
import Years from "./Years";
import Summary from "./Summary";
import Table from "../Table";
import Tooltip from "../Tooltip/";

import mapToNest from "../mapToNest.js";
import { getCountryByName } from "../countryLookup.js";
import { geoKey, colors, locations } from "../settings.js";
import { color as d3Color } from "d3-color";

import "./styles.scss";

let aspectRatio = 0.4;

const extraGradients = {};

const colorScale = scaleLinear().range(["#FDF5F5", "#CC0000"]);
const capacityColorScale = scaleLinear().range(["#ffffff", "#4d4d4d"]);

const yScales = {
  regions: scaleLinear(),
  countries: scaleLinear(),
  states: scaleLinear(),
  years: scaleLinear(),
};

const metricMap = {
  capacity: {
    groupKey: "countryID",
    valueKey: "capacity",
  },
  status: {
    groupKey: "status",
    valueKey: "capacity",
  },
  technology: {
    groupKey: "technology",
    valueKey: "capacity",
  },
  co2emissions: {
    groupKey: "technology",
    valueKey: "co2emissions",
  },
  lender: {
    groupKey: "lender",
    valueKey: "capacity",
  },
  dealtype: {
    groupKey: "dealtype",
    valueKey: "capacity",
  },
};

export default function Overview(props) {
  const {
    data,
    prevGeo,
    geoType,
    geoSelection,
    noTransform,
    updateGeo,
    metric,
    yearSelection,
    setYearSelection,
    lockedProject,
    setLockedProject,
  } = props;

  const lockedRowRef = useRef(null);
  const [transition, setTransition] = useState(false);
  const [tableTransition, setTableTransition] = useState(false);

  const [tableValueKey, setTableValueKey] = useState(
    metricMap[metric].valueKey
  );
  const [tooltipData, setTooltipData] = useState(null);
  const [mapSize, setMapSize] = useState({
    width: document.body.clientWidth,
    height: document.body.clientWidth * aspectRatio,
  });

  // Add state to track which year is being viewed in detail
  const [focusedYear, setFocusedYear] = useState(null);

  useEffect(() => {
    let delayHandle;
    if (transition) {
      setTableTransition(true);
      delayHandle = setTimeout(() => setTableTransition(false), 1100);
    }
    return () => (transition ? null : clearTimeout(delayHandle));
  }, [transition]);

  useEffect(() => {
    const { valueKey } = metricMap[metric];
    setTableValueKey(valueKey);
  }, [metric]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    function _measure() {
      const width = document.body.clientWidth;
      if (width <= 480) {
        aspectRatio = 0.75;
      } else {
        aspectRatio = (window.innerHeight - 288) / width <= 0.4 ? 0.36 : 0.4;
      }
      const height = width * aspectRatio;
      setMapSize({ width, height });
    }
    _measure();
    const debouncedMeasure = debounce(_measure, 100, {
      leading: true,
      trailing: true,
    });
    window.addEventListener("resize", debouncedMeasure);
    return function cleanup() {
      window.removeEventListener("resize", debouncedMeasure);
    };
  }, []);

  const isCapacity = metric === "capacity";
  const hideStacks = isCapacity && geoType !== "states";

  const selectedGeos = useMemo(() => {
    let selection = [""];
    if (geoSelection !== "") {
      selection =
        geoType === "regions" || (geoType === "states" && prevGeo === "regions")
          ? data.regions.find((r) => r.key === geoSelection).values
          : [geoSelection === "" ? "" : getCountryByName(geoSelection).id];
    }
    return selection;
  }, [geoSelection, geoType, prevGeo, data.regions]);
  // console.log(yearSelection)
  // Filter by Geo
  const filterGeoType = geoType === "states" ? prevGeo : geoType;
  const yearInvestments = useMemo(
    () =>
      data.investments.filter((d) =>
        geoSelection === "" ? true : geoKey[filterGeoType](d) === geoSelection
      ),
    [data.investments, filterGeoType, geoSelection]
  );
  // console.log(yearInvestments)
  // Filter by Year
  const investments = useMemo(
    () =>
      yearInvestments.filter((d) => {
        const unitHasAnyYear = d.units.some((unit) =>
          yearSelection.includes(unit.year)
        );
        return unitHasAnyYear;
      }),
    [yearInvestments, yearSelection]
  );
  // console.log(investments)
  // Nest by Geo
  const activeData = useMemo(
    () =>
      mapToNest(group(investments, (d) => geoKey[geoType](d))).map((d) => ({
        id: d.key,
        investments: d.values,
      })),
    [geoType, investments]
  );

  const fillSelection = useMemo(() => {
    let selection = {};
    if (hideStacks) {
      const values = mapToNest(
        group(
          data.investments.filter((d) =>
            d.units.some((unit) => yearSelection.includes(unit.year))
          ),
          (d) => geoKey[geoType](d)
        )
      )
        .map((d) => ({ id: d.key, investments: d.values }))
        .map((c) => ({
          name: c.id,
          total: c.investments
            .map((v) => v.capacity)
            .reduce((a, v) => a + v, 0),
        }));
      colorScale.domain(extent(values.map((v) => v.total)));
      values.forEach((v) => (selection[v.name] = colorScale(v.total)));
    }
    return selection;
  }, [hideStacks, data.investments, yearSelection, geoType]);

  const { groupKey, valueKey } = metricMap[metric];

  const capacityExtent = [Number.MAX_SAFE_INTEGER, Number.MIN_SAFE_INTEGER];
  activeData.forEach((locationGroup) => {
    locationGroup.investments.forEach((investment) => {
      const capacity = investment.capacity;
      capacityExtent[0] = Math.min(capacityExtent[0], capacity);
      capacityExtent[1] = Math.max(capacityExtent[1], capacity);
    });
  });
  capacityColorScale.domain(capacityExtent);

  const stacks = useMemo(() => {
    // Keep track of all missing locations
    const missingLocations = [];

    return activeData.map((d) => {
      const counts = {};
      const groups = sortBy(
        sortBy(
          mapToNest(
            group(d.investments.map((d) => d.units).flat(), (g) =>
              geoType === "states" ? g.plant : g[groupKey]
            )
          )
            .map((g) => {
              const key = geoType === "states" ? g.values[0][groupKey] : g.key;
              let color = colors[key]
                ? colors[key].light
                : "rgba(255, 255, 255, 0.25)";
              const count = g.values
                .map((v) => v[valueKey])
                .reduce((a, v) => a + v, 0);
              let fill = `url(#${key.replace(/ /g, "-")})`;
              if (geoType === "states" && metric === "capacity") {
                const capacity = g.values[0].capacity;
                const scaledColor = d3Color(capacityColorScale(capacity));
                color = scaledColor.formatHex();
                const colorId = `extraGradient-${color.replace("#", "")}`;
                fill = `url(#${colorId})`;
                const dark = scaledColor.darker().formatHex();
                if (!extraGradients[color]) {
                  extraGradients[color] = (
                    <linearGradient
                      key={colorId}
                      id={colorId}
                      x1="0%"
                      x2="50%"
                      y1="0%"
                      y2="50%"
                      gradientTransform={"skewX(45)"}
                    >
                      <stop offset="0%" stopColor={dark} />
                      <stop offset="33%" stopColor={dark} />
                      <stop offset="34%" stopColor={color} />
                      <stop offset="100%" stopColor={color} />
                    </linearGradient>
                  );
                }
              }
              if (counts[color]) {
                counts[color] += count;
              } else {
                counts[color] = count;
              }
              return {
                key: `${d.id}-${g.key}`,
                name: key,
                count,
                color,
                fill,
                projects: hideStacks ? [] : g.values,
              };
            })
            .filter((g) => g.name !== "NA" && g.count > 0),
          (g) => -g.count
        ),
        (g) => (metric === "capacity" ? 0 : -counts[g.color])
      );
      const total = groups.map((g) => g.count).reduce((a, v) => a + v, 0);

      // Try to get location from the specified geoType
      let location = locations[geoType][d.id];

      // If state/region not found but we're looking at state level, try to get country coordinates
      if (!location && geoType === "states") {
        // Extract country from the state/region name (assumes format like "Region-Country")
        const parts = d.id.split("-");
        if (parts.length > 1) {
          // Get the last part which should be the country name
          const countryPart = parts[parts.length - 1];

          // Try different variations of the country name
          let countryName = countryPart;
          let countryLocation = locations.countries[countryName];

          // If not found, try United States variants
          if (
            !countryLocation &&
            (countryPart === "United-States" || countryPart === "USA")
          ) {
            countryLocation = locations.countries["United States of America"];
          }

          // Try with spaces instead of hyphens
          if (!countryLocation) {
            countryName = countryPart.replace(/-/g, " ");
            countryLocation = locations.countries[countryName];
          }

          // Check for common country code abbreviations
          if (!countryLocation) {
            // Handle specific country codes
            const countryCodeMap = {
              UK: "United Kingdom",
              USA: "United States of America",
              UAE: "United Arab Emirates",
            };

            if (countryCodeMap[countryPart]) {
              countryLocation =
                locations.countries[countryCodeMap[countryPart]];
              countryName = countryCodeMap[countryPart];
            }
          }

          if (countryLocation) {
            location = countryLocation;
            console.warn(
              `Using country coordinates for missing location: ${d.id} -> ${countryName}`
            );
          }
        }
      }

      // Default to origin if no location found
      if (!location) {
        location = {
          latitude: 0,
          longitude: 0,
        };
        // Track all missing locations for later reference
        missingLocations.push({
          id: d.id,
          geoType,
          totalCapacity: total,
        });
        console.warn(`Missing location at ${geoType} level for ${d.id}`);
      }

      return {
        name: d.id,
        groups,
        total,
        location,
      };
    });

    // After processing all locations, log a summary of missing ones
    if (missingLocations.length > 0) {
      console.error(`MISSING LOCATIONS (${missingLocations.length}):`);
      // Sort by total capacity to prioritize the most important ones
      const sortedMissing = sortBy(missingLocations, (m) => -m.totalCapacity);
      console.table(sortedMissing);

      // Generate template code for adding these locations to settings.js
      console.error("Copy this list to add missing locations to settings.js");
      console.error(JSON.stringify(sortedMissing.map((m) => m.id)));

      // Generate template code block for settings.js
      const generateLocationTemplates = (locations) => {
        // Group locations by geoType
        const byType = {};
        locations.forEach((loc) => {
          if (!byType[loc.geoType]) byType[loc.geoType] = [];
          byType[loc.geoType].push(loc.id);
        });

        // Generate template for each type
        Object.keys(byType).forEach((type) => {
          console.error(
            `\n---- TEMPLATE FOR ${type.toUpperCase()} LOCATIONS ----`
          );
          const template = byType[type]
            .map((id) => `    "${id}": { latitude: 0, longitude: 0 },`)
            .join("\n");
          console.error(template);
        });
      };

      generateLocationTemplates(sortedMissing);
    }
  }, [geoType, activeData, groupKey, valueKey, hideStacks, metric]);

  // yScale(s) for map
  const maxTotal = useMemo(
    () => Math.max(...stacks.map((s) => s.total)),
    [stacks]
  );
  const range = useMemo(
    () =>
      geoType === "states" ? [2, mapSize.height / 6] : [0, mapSize.height / 4],
    [geoType, mapSize.height]
  );
  const baseYearRange = useMemo(
    () => [0, mapSize.height / 4],
    [mapSize.height]
  );
  yScales[geoType].domain([0, maxTotal]).range(range);

  // Summary & Table data
  const summaryGeoType = useMemo(() => {
    if (geoType === "states") {
      if (geoSelection === "") {
        return prevGeo;
      } else if (prevGeo === "regions") {
        return "countries";
      }
    }
    return geoType;
  }, [geoType, geoSelection, prevGeo]);
  // YEARS
  const years = useMemo(() => {
    const investmentsFlat = yearInvestments.map((d) => d.units).flat();

    // Get the set of all years to be consistent with Summary
    const yearsSet = new Set();
    investmentsFlat.forEach((unit) => {
      yearsSet.add(unit.year);
    });

    let investmentsByYear = mapToNest(
      group(investmentsFlat, (d) => d.year)
    ).map((d) => {
      const groups = sortBy(
        mapToNest(
          group(d.values, (g) => (isCapacity ? "capacity" : g[groupKey]))
        )
          .map((g) => {
            let projects = sortBy(
              g.values.map((project) => {
                project.count = project[valueKey];
                return project;
              }),
              (project) => -project[valueKey]
            );

            // Use the same rollup approach as in the Summary calculation
            projects = rollups(
              projects,
              (v) => {
                const combinedProject = { ...v[0] };
                // Ensure we're summing the values correctly, matching Summary approach
                combinedProject.count = v
                  .map((d) => d.count)
                  .reduce((a, v) => Number(a) + Number(v), 0);
                return combinedProject;
              },
              (d) => d.plant
            ).map((d) => d[1]);

            return {
              key: g.key,
              name: g.key,
              fill: colors[g.key] ? colors[g.key].light : "#C00",
              count: projects
                .map((v) => v.count)
                .reduce((a, v) => Number(a) + Number(v), 0),
              projects,
            };
          })
          .filter((g) => g.name !== "NA" && g.count > 0),
        "count"
      );

      // Calculate total using the same Number() conversion as in Summary
      const total = groups
        .map((g) =>
          g.projects
            .map((p) => p.count)
            .reduce((a, v) => Number(a) + Number(v), 0)
        )
        .reduce((a, v) => Number(a) + Number(v), 0);

      return {
        name: d.key,
        groups,
        total,
      };
    });

    // yScale for years
    const yearMaxTotal = Math.max(
      Math.max(
        ...investmentsByYear.map((y) =>
          y.groups
            .map((g) =>
              g.projects
                .map((p) => p.count)
                .reduce((a, v) => Number(a) + Number(v), 0)
            )
            .reduce((a, v) => Number(a) + Number(v), 0)
        )
      ),
      0
    );
    const yearRange = geoType === summaryGeoType ? range : baseYearRange;
    yScales.years.domain([0, yearMaxTotal]).range(yearRange);

    // assign heights and offsets
    investmentsByYear = investmentsByYear.map((y) => {
      let runningHeight = 0;
      y.groups = y.groups.map((g) => {
        g.projects = g.projects.map((p) => {
          p.height = Math.max(1, Math.round(yScales.years(p.count)));
          p.top = runningHeight;
          runningHeight += p.height;
          return p;
        });
        return g;
      });
      y.height = runningHeight;
      return y;
    });

    const includedYears = investmentsByYear.map((d) => d.name);
    const missingYears = data.years.filter((y) => !includedYears.includes(y));
    missingYears.forEach((y) =>
      investmentsByYear.push({ name: y, groups: [], total: 0, height: 0 })
    );
    return sortBy(investmentsByYear, (y) => (y.name === "TBD" ? 3000 : y.name));
  }, [
    yearInvestments,
    isCapacity,
    groupKey,
    valueKey,
    data.years,
    geoType,
    summaryGeoType,
    range,
    baseYearRange,
  ]);

  // We need to ensure the Summary component gets data consistent with the Years component
  const [tableSummary, summary] = useMemo(() => {
    // Use the same source data and calculations as the Years component
    const yearsData = yearInvestments.map((d) => d.units).flat();
    const investmentsData = investments.map((d) => d.units).flat();

    // Filter by focused year if one is selected
    const filteredData = focusedYear
      ? investmentsData.filter((d) => d.year === focusedYear)
      : investmentsData;

    console.log(
      `Processing data for ${
        focusedYear ? `year ${focusedYear}` : "all selected years"
      }`
    );
    console.log(`Units count in filteredData: ${filteredData.length}`);

    // IMPORTANT: Use the same data source for both tableSummary and summary to ensure consistency
    return [filteredData, filteredData].map((data, index) => {
      const result = sortBy(
        mapToNest(group(data, (d) => (isCapacity ? "capacity" : d[groupKey])))
          .map((d) => {
            const geos = sortBy(
              mapToNest(group(d.values, (d) => geoKey[summaryGeoType](d))).map(
                (g) => {
                  const projects = sortBy(
                    g.values.map((project) => {
                      project.name = project.plant;
                      return project;
                    }),
                    (project) =>
                      project[tableValueKey] === "TBD"
                        ? 3000
                        : project[tableValueKey]
                  ).reverse();

                  const [year] = sortBy(
                    projects.map((p) => p.year),
                    (y) => -y
                  );
                  const name =
                    summaryGeoType === "states" ? projects[0].state : g.key;
                  return {
                    name,
                    capacity: g.values
                      .map((v) => v.capacity)
                      .reduce((a, v) => Number(a) + Number(v), 0),
                    co2emissions: g.values
                      .map((v) => v.co2emissions)
                      .reduce((a, v) => Number(a) + Number(v), 0),
                    value: g.values
                      .map((v) => v[valueKey])
                      .reduce((a, v) => Number(a) + Number(v), 0),
                    year,
                    projects,
                  };
                }
              ),
              (g) =>
                g.hasOwnProperty(tableValueKey)
                  ? g[tableValueKey] === "TBD"
                    ? 3000
                    : g[tableValueKey]
                  : g[valueKey]
            ).reverse();
            const [year] = sortBy(
              geos.map((g) => g.year),
              (y) => -y
            );

            const capacity = d.values
              .map((v) => v.capacity)
              .reduce((a, v) => Number(a) + Number(v), 0);

            const co2emissions = d.values
              .map((v) => v.co2emissions)
              .reduce((a, v) => Number(a) + Number(v), 0);

            const value = d.values
              .map((v) => v[valueKey])
              .reduce((a, v) => Number(a) + Number(v), 0);

            // Debug values for coal and gas
            if ((d.key === "Coal" || d.key === "Gas") && index === 0) {
              console.log(
                `${d.key} in ${
                  index === 0 ? "tableSummary" : "summary"
                }: capacity=${capacity}, value=${value}`
              );
            }

            return {
              name: d.key,
              capacity,
              co2emissions,
              value,
              fill: colors[d.key] ? colors[d.key].light : "#C00",
              groupFill: colors[d.key] ? colors[d.key].transparent : "#E5E5E5",
              geos,
              year,
            };
          })
          .filter((g) => g.name !== "NA" && g.value > 0),
        (d) =>
          d.hasOwnProperty(tableValueKey)
            ? d[tableValueKey] === "TBD"
              ? 3000
              : d[tableValueKey]
            : d[valueKey]
      ).reverse();

      // Debug total
      if (index === 0) {
        let totalCapacity = 0;
        result.forEach((item) => {
          totalCapacity += Number(item.capacity || 0);
        });
        console.log(
          `Total capacity in ${
            index === 0 ? "tableSummary" : "summary"
          }: ${totalCapacity}`
        );
      }

      return result;
    });
  }, [
    investments,
    yearInvestments,
    isCapacity,
    summaryGeoType,
    groupKey,
    valueKey,
    tableValueKey,
    focusedYear,
    geoKey,
  ]);

  // This is the data that's actually used by the Table component
  const fixedTableSummary = useMemo(() => {
    console.log(
      "Building fixedTableSummary with data from the same source as Summary"
    );

    // Start with a deep copy of tableSummary to ensure we're working with the same data
    // but don't modify the original
    const singleGroups = JSON.parse(
      JSON.stringify(tableSummary.filter((d) => !d.name.includes(",")))
    );

    // Process groups with multiple technology values
    const multipleGroups = tableSummary.filter((d) => d.name.includes(","));

    if (multipleGroups.length > 0) {
      console.log(
        `Processing ${multipleGroups.length} items with multiple technology values`
      );

      multipleGroups.forEach((tableGroup) => {
        tableGroup.geos.forEach((geo) => {
          geo.projects.forEach((project) => {
            // Only process units for the focused year if one is selected
            const unitsToProcess = project.units
              ? project.units.filter(
                  (unit) => !focusedYear || unit.year === focusedYear
                )
              : [];

            unitsToProcess.forEach((unit) => {
              const capacity = Number(unit.capacity || 0);
              const co2emissions = Number(unit.co2emissions || 0);
              const value = Number(unit[valueKey] || 0);

              // Skip processing if the unit has no capacity or value
              if (capacity === 0 && value === 0) return;

              let matchingSummaryItem = singleGroups.find(
                (d) => d.name === unit[groupKey]
              );

              if (!matchingSummaryItem) {
                matchingSummaryItem = {
                  name: unit[groupKey],
                  capacity: 0,
                  co2emissions: 0,
                  value: 0,
                  fill: colors[unit[groupKey]]
                    ? colors[unit[groupKey]].light
                    : "#C00",
                  groupFill: colors[unit[groupKey]]
                    ? colors[unit[groupKey]].transparent
                    : "#E5E5E5",
                  geos: [],
                };
                singleGroups.push(matchingSummaryItem);
              }

              matchingSummaryItem.capacity += capacity;
              matchingSummaryItem.co2emissions += co2emissions;
              matchingSummaryItem.value += value;

              let matchingGeo = matchingSummaryItem.geos.find(
                (d) => d.name === geo.name
              );

              if (!matchingGeo) {
                matchingGeo = {
                  name: geo.name,
                  capacity: 0,
                  co2emissions: 0,
                  value: 0,
                  projects: [],
                };
                matchingSummaryItem.geos.push(matchingGeo);
              }

              matchingGeo.capacity += capacity;
              matchingGeo.co2emissions += co2emissions;
              matchingGeo.value += value;

              // Handle projects within geo
              const projectCopy = {
                ...project,
                capacity: Number(project.capacity || 0),
                co2emissions: Number(project.co2emissions || 0),
                value: Number(project[valueKey] || 0),
              };

              let matchingProject = matchingGeo.projects.find(
                (d) => d.name === project.name
              );

              if (!matchingProject) {
                matchingGeo.projects.push(projectCopy);
              }
            });
          });
        });
      });
    }

    // Final debug check of the key values
    const coalItem = singleGroups.find((item) => item.name === "Coal");
    if (coalItem) {
      console.log(
        `Final Coal in fixedTableSummary: capacity=${coalItem.capacity}, value=${coalItem.value}`
      );
    }

    const gasItem = singleGroups.find((item) => item.name === "Gas");
    if (gasItem) {
      console.log(
        `Final Gas in fixedTableSummary: capacity=${gasItem.capacity}, value=${gasItem.value}`
      );
    }

    // Sort geos in each item by value
    singleGroups.forEach((item) => {
      if (item.geos) {
        item.geos.sort((a, b) => Number(b.value) - Number(a.value));

        // Sort projects in each geo by value
        item.geos.forEach((geo) => {
          if (geo.projects) {
            geo.projects.sort(
              (a, b) => Number(b[valueKey]) - Number(a[valueKey])
            );
          }
        });
      }
    });

    // Calculate and log total capacity
    let totalCapacity = 0;
    singleGroups.forEach((item) => {
      totalCapacity += Number(item.capacity || 0);
    });
    console.log(`Total capacity in fixedTableSummary: ${totalCapacity}`);

    return singleGroups;
  }, [tableSummary, groupKey, valueKey, colors, focusedYear]);

  const updateLockedProject = useMemo(
    () =>
      function (project, scroll = false, tooltipFromMap = false) {
        project.lockedFromMap = tooltipFromMap;
        setLockedProject((lockedProject) => {
          const unlock = lockedProject
            ? lockedProject.plantID === project.plantID
            : false;
          if (!unlock && scroll) {
            window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
          }
          return unlock ? null : project;
        });
      },
    [setLockedProject]
  );

  const lastHoveredTooltipPosition = useRef({});

  if (tooltipData) {
    let updatePosition = true;
    if (lockedProject) {
      const lockedProjectKey = `${lockedProject.stateID}-${lockedProject.plant}`;
      if (lockedProjectKey === tooltipData.data.key) {
        updatePosition = false;
      }
    }
    if (updatePosition) {
      lastHoveredTooltipPosition.current[tooltipData.data.key] = {
        x: tooltipData.x,
        y: tooltipData.y,
      };
    }
  }

  let lockedProjectTooltipData = null;
  if (lockedProject) {
    const stackKey = `${lockedProject.stateID}-${lockedProject.plant}`;
    const stackLocation = stackLocations[stackKey];
    if (stackLocation) {
      const lockedProjectKey = `${lockedProject.stateID}-${lockedProject.plant}`;
      lockedProjectTooltipData = {
        data: { projects: [lockedProject] },
        type: "project",
        lockable: true,
        valueKey,
        x: stackLocation.screen[0] + stackLocation.translate.x,
        y:
          stackLocation.screen[1] +
          stackLocation.translate.y +
          props.headerHeight / stackLocations.transform.scale,
        mapPositioned: true,
      };
      if (
        lockedProject.lockedFromMap &&
        lastHoveredTooltipPosition.current[lockedProjectKey]
      ) {
        lockedProjectTooltipData.x =
          lastHoveredTooltipPosition.current[lockedProjectKey].x;
        lockedProjectTooltipData.y =
          lastHoveredTooltipPosition.current[lockedProjectKey].y;
        lockedProjectTooltipData.mapPositioned = false;
      }
    }
  }

  const mapGeo = useMemo(() => ({ prevGeo, geo: geoType }), [prevGeo, geoType]);

  const updateGeoType = useMemo(
    () =>
      function (value) {
        return updateGeo("type", value);
      },
    [updateGeo]
  );

  const updateGeoSelection = useMemo(
    () =>
      function (value) {
        return updateGeo("selection", value);
      },
    [updateGeo]
  );

  const [relevantTooltip, setRelevantTooltip] = useState(null);
  useEffect(() => {
    if (tooltipData && tooltipData.type === "project") {
      setRelevantTooltip(tooltipData);
    } else if (relevantTooltip !== null) {
      setRelevantTooltip(null);
    }
  }, [relevantTooltip, tooltipData]);

  // Add debug logging for years data
  useEffect(() => {
    if (years && years.length > 0) {
      console.log("Debug Overview - Years data:");

      // Calculate total across all years
      let yearsTotal = 0;
      years.forEach((year) => {
        if (year.total) {
          yearsTotal += year.total;
        }
      });

      console.log(`Years total across all years: ${yearsTotal} ${valueKey}`);
    }
  }, [years, valueKey]);

  // Add debug logging for summary data
  useEffect(() => {
    if (summary && summary.length > 0) {
      console.log("Debug Overview - Summary data:");

      // Calculate total across all summary items
      let summaryTotal = 0;
      summary.forEach((item) => {
        if (item.value) {
          summaryTotal += item.value;
          console.log(`Summary item ${item.name}: ${item.value} ${valueKey}`);
        }
      });

      console.log(
        `Summary total across all items: ${summaryTotal} ${valueKey}`
      );
    }
  }, [summary, valueKey]);

  // Add debug to compare data sources
  useEffect(() => {
    if (years && years.length > 0 && summary && summary.length > 0) {
      console.log("DEBUG: Comparing data sources between Years and Summary");

      // Calculate years total
      let yearsTotal = 0;
      years.forEach((year) => {
        if (year.total) {
          yearsTotal += year.total;
        }
      });

      // Calculate summary total
      let summaryTotal = 0;
      summary.forEach((item) => {
        if (item.value) {
          summaryTotal += item.value;
        }
      });

      // Compare and log difference
      const difference = Math.abs(yearsTotal - summaryTotal);
      console.log(`Years total: ${yearsTotal}, Summary total: ${summaryTotal}`);
      console.log(
        `Difference: ${difference} (${((difference / yearsTotal) * 100).toFixed(
          2
        )}%)`
      );

      if (difference > 0.1) {
        console.log(
          "WARNING: Significant difference between Years and Summary totals"
        );

        // Let's examine the data sources
        console.log("Examining data sources:");
        console.log("yearInvestments count:", yearInvestments.length);
        console.log("investments count:", investments.length);

        // Compare the year sets
        const yearNames = new Set(years.map((y) => y.name));
        console.log("Years in bar chart:", [...yearNames]);

        // Check for projects with missing years
        if (yearInvestments && yearInvestments.length > 0) {
          const yearsWithValues = new Set();
          yearInvestments.forEach((inv) => {
            inv.units.forEach((unit) => {
              yearsWithValues.add(unit.year);
            });
          });
          console.log("Years with values in yearInvestments:", [
            ...yearsWithValues,
          ]);
        }
      }
    }
  }, [years, summary, yearInvestments, investments, valueKey]);

  // Add debug log to verify our fix
  useEffect(() => {
    if (years && years.length > 0 && summary && summary.length > 0) {
      console.log("VERIFICATION: Checking if discrepancy is fixed");

      // Calculate years total
      let yearsTotal = 0;
      years.forEach((year) => {
        if (year.total) {
          yearsTotal += year.total;
        }
      });

      // Calculate summary total
      let summaryTotal = 0;
      summary.forEach((item) => {
        if (item.value) {
          summaryTotal += item.value;
        }
      });

      // Compare and log difference
      const difference = Math.abs(yearsTotal - summaryTotal);
      const percentDiff = (
        (difference / Math.max(yearsTotal, summaryTotal)) *
        100
      ).toFixed(2);

      console.log(
        `FIXED VALUES - Years total: ${yearsTotal}, Summary total: ${summaryTotal}`
      );
      console.log(`FIXED DIFFERENCE: ${difference} (${percentDiff}%)`);

      if (difference < 0.1 || percentDiff < 0.1) {
        console.log("SUCCESS: Discrepancy fixed! The totals now match.");
      } else {
        console.log("WARNING: Discrepancy still exists but should be reduced.");
      }
    }
  }, [years, summary]);

  // Add debug function to track year focus changes
  useEffect(() => {
    if (focusedYear) {
      console.log("Year focused:", focusedYear);
    } else {
      console.log("No year focused - showing all selected years data");
    }

    // Perform an additional check to verify summary and tableSummary alignment
    if (summary && tableSummary) {
      console.log(
        "Verifying data alignment between Summary and Table components:"
      );

      // Get total capacities
      let summaryCapacity = 0;
      let tableSummaryCapacity = 0;

      summary.forEach(
        (item) => (summaryCapacity += Number(item.capacity || 0))
      );
      tableSummary.forEach(
        (item) => (tableSummaryCapacity += Number(item.capacity || 0))
      );

      console.log(`Summary capacity: ${summaryCapacity}`);
      console.log(`TableSummary capacity: ${tableSummaryCapacity}`);
      console.log(
        `Difference: ${Math.abs(summaryCapacity - tableSummaryCapacity)}`
      );

      // Check specific technologies
      const findItem = (arr, name) => arr.find((item) => item.name === name);

      const checkTechnology = (techName) => {
        const summaryItem = findItem(summary, techName);
        const tableItem = findItem(tableSummary, techName);

        if (summaryItem && tableItem) {
          console.log(
            `${techName} - Summary: ${summaryItem.capacity}, Table: ${tableItem.capacity}`
          );
          if (
            Math.abs(
              Number(summaryItem.capacity) - Number(tableItem.capacity)
            ) > 0.1
          ) {
            console.warn(
              `MISMATCH: ${techName} values are different between Summary and Table`
            );
          }
        }
      };

      checkTechnology("Coal");
      checkTechnology("Gas");
    }
  }, [focusedYear, summary, tableSummary]);

  return (
    <div className="Overview">
      <Map
        transition={transition}
        setTransition={setTransition}
        valueKey={valueKey}
        stacks={hideStacks ? [] : stacks}
        years={years}
        regions={data.regions}
        yScale={yScales[geoType]}
        width={mapSize.width}
        height={mapSize.height}
        fillSelection={fillSelection}
        geoType={mapGeo}
        updateGeoType={updateGeoType}
        noTransform={noTransform}
        geoSelection={selectedGeos}
        updateGeoSelection={updateGeoSelection}
        selectedYears={yearSelection}
        extraGradients={Object.keys(extraGradients).map(
          (key) => extraGradients[key]
        )}
        setTooltipData={setTooltipData}
        lockedProject={lockedProject}
        updateLockedProject={updateLockedProject}
        colorScale={hideStacks ? colorScale : null}
      />
      <Years
        delay={transition}
        years={years}
        valueKey={valueKey}
        width={mapSize.width}
        allYears={data.years}
        selectedYears={yearSelection}
        updateYears={setYearSelection}
        relevantTooltip={relevantTooltip}
        lockedProject={lockedProject}
        updateLockedProject={updateLockedProject}
        groupKey={groupKey}
        language={props.language}
        setFocusedYear={setFocusedYear}
        focusedYear={focusedYear}
      />
      <Summary
        delay={transition}
        data={summary}
        valueKey={valueKey}
        width={mapSize.width}
        isCapacity={isCapacity}
        geoSelection={geoSelection}
        language={props.language}
        focusedYear={focusedYear}
      />
      <Table
        delay={transition || tableTransition}
        data={summary}
        groupKey={groupKey}
        valueKey={tableValueKey}
        setValueKey={setTableValueKey}
        isCapacity={isCapacity}
        geoSelection={[geoSelection]}
        selectedYears={yearSelection}
        allYears={data.years}
        setTooltipData={setTooltipData}
        lockable={true}
        lockedProject={lockedProject}
        updateLockedProject={updateLockedProject}
        lockedRowRef={lockedRowRef}
        language={props.language}
      />
      {lockedProject && lockedProjectTooltipData ? (
        <div
          className="lockedTooltipWrapper"
          style={{
            transform: lockedProjectTooltipData.mapPositioned
              ? `translate(${stackLocations.transform.translate.x}px, ${stackLocations.transform.translate.y}px) scale(${stackLocations.transform.scale})`
              : null,
          }}
        >
          <Tooltip
            {...lockedProjectTooltipData}
            width={document.body.clientWidth}
            height={window.innerHeight}
            selectedYears={yearSelection}
            lockedProject={lockedProject}
            updateLockedProject={updateLockedProject}
            lockedRowRef={lockedRowRef}
            lockedTooltip
            wrapperStyle={{
              transformOrigin: "top left",
              transform: lockedProjectTooltipData.mapPositioned
                ? `scale(${1 / stackLocations.transform.scale})`
                : null,
            }}
          />
        </div>
      ) : null}
      {tooltipData && !(lockedProject && tooltipData.hoveredFromMap) ? (
        <Tooltip
          {...tooltipData}
          width={document.body.clientWidth}
          height={window.innerHeight}
          selectedYears={yearSelection}
          lockedProject={lockedProject}
        />
      ) : null}
    </div>
  );
}
